import type { SyntheticEvent } from 'react';
import { WEB_BASE_URL } from '../config/environment';

export const PLACEHOLDER_IMAGE = '/products/placeholder.svg';

// Swap a broken <img> to the placeholder exactly once, so a failing placeholder
// can't retrigger onError and reload forever.
export const handleImageError = (e: SyntheticEvent<HTMLImageElement>) => {
  const img = e.currentTarget;
  if (img.dataset.fallback) return;
  img.dataset.fallback = '1';
  img.src = PLACEHOLDER_IMAGE;
};

// Bucket URLs (Tigris/Railway, Cloudflare R2) → route through our proxy; r2.dev is
// rate-limited and blocked on some networks. Any other URL is returned unchanged.
export const proxyBucketUrl = <T extends string | undefined>(url: T): T => {
  if (url && (url.includes('storageapi.dev') || url.includes('railway.storage') || url.includes('.r2.dev') || url.includes('r2.cloudflarestorage.com'))) {
    return `${WEB_BASE_URL}/api/upload/proxy/${url.replace(/^https?:\/\/[^/]+\//, '')}` as T;
  }
  return url;
};

// Local paths (/products/...) are served by the backend, which in dev runs on a
// different port than the frontend
const withBackendBase = (url: string): string =>
  url.startsWith('/products/') ? `${WEB_BASE_URL}${url}` : url;

// Utility function to get the correct product image URL
export const getProductImageUrl = (product: any, imageIndex: number = 0): string =>
  withBackendBase(resolveProductImage(product, imageIndex));

const resolveProductImage = (product: any, imageIndex: number): string => {
  // If product or images don't exist, return placeholder
  if (!product || !product.images || !Array.isArray(product.images) || product.images.length === 0) {
    return '/products/placeholder.svg';
  }

  const image = product.images[imageIndex];
  
  // If the specific image index doesn't exist, try the first image
  if (!image && product.images.length > 0) {
    const firstImage = product.images[0];
    if (!firstImage) {
      return '/products/placeholder.svg';
    }
    return ensureCorrectImagePath(firstImage);
  }

  // If no image at all, return placeholder
  if (!image) {
    return '/products/placeholder.svg';
  }

  return ensureCorrectImagePath(image);
};

// Ensure the image path is correctly formatted for the public folder
const ensureCorrectImagePath = (imagePath: string): string => {
  if (!imagePath) {
    return '/products/placeholder.svg';
  }

  const proxied = proxyBucketUrl(imagePath);
  if (proxied !== imagePath) {
    return proxied;
  }

  // Our proxy URL or other absolute URLs
  if (imagePath.startsWith('https://') || imagePath.startsWith('http://') || imagePath.startsWith('/api/upload/proxy/')) {
    return imagePath;
  }

  // If it already starts with /products/, use as is
  if (imagePath.startsWith('/products/')) {
    return imagePath;
  }

  // If it starts with products/ (without leading slash), add the slash
  if (imagePath.startsWith('products/')) {
    return `/${imagePath}`;
  }

  // If it's just the filename, prepend /products/
  if (!imagePath.startsWith('/') && !imagePath.includes('/')) {
    return `/products/${imagePath}`;
  }

  // If it starts with /, but not /products/, assume it's a product image
  if (imagePath.startsWith('/') && !imagePath.startsWith('/products/')) {
    return `/products${imagePath}`;
  }

  // Default case - assume it needs /products/ prefix
  return `/products/${imagePath}`;
};

// Function to check if an image exists and provide fallback
export const getImageWithFallback = (imageSrc: string): string => {
  if (!imageSrc) {
    return '/products/placeholder.svg';
  }
  
  // For now, just return the image source
  // In a more robust implementation, you could check if the image exists
  return imageSrc;
};

// Helper function to safely parse product images (handles JSON string or array)
export const getProductImages = (images: any): string[] => {
  if (!images) return [];
  
  // If it's already an array, return it
  if (Array.isArray(images)) {
    return images;
  }
  
  // If it's a string, try to parse as JSON
  if (typeof images === 'string') {
    try {
      const parsed = JSON.parse(images);
      return Array.isArray(parsed) ? parsed : [images];
    } catch {
      // If JSON parsing fails, treat as a single image
      return [images];
    }
  }
  
  return [];
};

// Helper function to get the first image safely with JSON parsing support
export const getFirstProductImage = (images: any, fallback: string = ''): string => {
  const imageArray = getProductImages(images);
  const firstImage = imageArray[0];
  
  if (!firstImage) {
    console.log('No image found for product:', images);
    return fallback;
  }
  
  const processedImage = ensureCorrectImagePath(firstImage);
  console.log('Processed image URL:', processedImage);
  return processedImage;
};

// Downscale + re-encode an image in the browser before uploading, so huge phone
// photos (48MP iPhone shots, HEIC from iPad/iPhone) stay well under the server
// upload limit. Falls back to the original file if the browser can't decode it.
export const compressImageFile = async (file: File, maxDim = 2000, quality = 0.85): Promise<File> => {
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    }).finally(() => URL.revokeObjectURL(url));

    const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob || (blob.size >= file.size && file.type === 'image/jpeg')) return file;
    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg' });
  } catch (err) {
    console.warn('Image compression failed, uploading original:', err);
    return file;
  }
};
